import ProgressiveBlurCircle from "@/components/decorations/ProgressiveBlurCircle.tsx";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter, { getNameInitials } from "@/hooks/use-formatter.hook.ts";
import { useAppContext } from "@/themes/app-context.ts";
import { ApiMeEmployee } from "@/types/employees/employee.types.ts";
import { ensureContrastOnWhite } from "@/utils/color-contrast.utils.ts";
import { Avatar, Box, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";

interface PreboardingProfileHeaderProps {
  employee: ApiMeEmployee;
  green?: boolean;
}

export function PreboardingProfileHeader({ employee, green }: PreboardingProfileHeaderProps) {
  const { renderEmployeeName } = useFormatter();
  const fullName = renderEmployeeName(employee);
  const [firstLetter = "", secondLetter = ""] = getNameInitials(fullName);
  const { appThemeConfig } = useAppContext().theme;

  const { colorSidebar, colorBackgroundGradientFrom, colorBackgroundGradientTo } = appThemeConfig;

  const { firstColor, secondColor, ringFirstColor } = useMemo(() => {
    const first = ensureContrastOnWhite(colorSidebar ?? colorBackgroundGradientFrom);
    return {
      firstColor: first,
      secondColor: ensureContrastOnWhite(colorBackgroundGradientTo),
      ringFirstColor: colorSidebar ? `hsl(${colorSidebar})` : first,
    };
  }, [colorSidebar, colorBackgroundGradientFrom, colorBackgroundGradientTo]);

  const ring1 = green
    ? { start: "hsla(154, 80%, 38%, 1)", end: "hsla(102, 100%, 62%, 1)" }
    : { start: ringFirstColor, end: secondColor };
  const ring2 = green
    ? { start: "hsla(165, 64%, 47%, 1)", end: "hsla(87, 63%, 48%, 1)" }
    : { start: secondColor, end: ringFirstColor };
  const ring3 = green
    ? { start: "hsla(165, 64%, 47%, 1)", end: "hsla(87, 63%, 48%, 1)" }
    : { start: ringFirstColor, end: secondColor };

  return (
    <Stack items={"center"} gap={"md"}>
      <Box className={"relative flex items-center justify-center"}>
        <Box
          className={"absolute top-1/2 left-1/2 pointer-events-none"}
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <ProgressiveBlurCircle
            radius={177}
            strokeWidth={361}
            startBlur={10}
            endBlur={54}
            startColor={ring3.start}
            endColor={ring3.end}
            angleBlur={0}
            angleGradient={80}
            strokeOpacity={0.1}
            colorSpace={"sRGB"}
          />
        </Box>
        <Box
          className={"absolute top-1/2 left-1/2 pointer-events-none"}
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <ProgressiveBlurCircle
            radius={191}
            strokeWidth={81}
            startBlur={10}
            endBlur={54}
            startColor={ring2.start}
            endColor={ring2.end}
            angleBlur={0}
            angleGradient={80}
            strokeOpacity={0.2}
            colorSpace={"sRGB"}
          />
        </Box>
        <Box
          className={"absolute top-1/2 left-1/2 pointer-events-none"}
          style={{ transform: "translate(-50%, -50%)" }}
        >
          <ProgressiveBlurCircle
            radius={92}
            strokeWidth={61}
            startBlur={10}
            endBlur={54}
            startColor={ring1.start}
            endColor={ring1.end}
            angleBlur={56.91}
            angleGradient={137}
            strokeOpacity={0.7}
            colorSpace={"sRGB"}
          />
        </Box>
        {employee.avatar_url ? (
          <EmployeeAvatar
            employee={employee}
            size={"2xl"}
            variant={"opaque"}
            className={"relative z-10 rounded-full"}
          />
        ) : (
          <Avatar
            size={"2xl"}
            variant={"opaque"}
            className={"relative z-10 rounded-full"}
            icon={
              <span className={"font-semibold"}>
                <span style={{ color: firstColor }}>{firstLetter}</span>
                <span style={{ color: secondColor }}>{secondLetter}</span>
              </span>
            }
          />
        )}
      </Box>
      <Title header={fullName} size={"lg"} className={"relative z-10 text-center"} />
    </Stack>
  );
}
